import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Users, Search, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useSortableTable } from '../lib/useSortableTable';

const ENG_NA_TIP = "Not enough data yet. Engagement is rated once more of this student's lessons have been analysed.";

const Students = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const perPage = 20;

  useEffect(() => {
    if (!organizationId) return;
    loadStudents();
  }, [organizationId]);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const { data: sessions } = await supabase
        .from('sessions')
        .select('student_name, tutor_name, lesson_timestamp, student_engagement_level')
        .eq('organization_id', organizationId)
        .eq('is_processed_v2', true);

      const map = {};
      for (const s of (sessions || [])) {
        const name = s.student_name;
        if (!name) continue;
        if (!map[name]) map[name] = { name, sessions: 0, tutors: new Set(), lastDate: null, engHigh: 0, engMed: 0, engLow: 0 };
        map[name].sessions++;
        if (s.tutor_name) map[name].tutors.add(s.tutor_name);
        if (s.lesson_timestamp && (!map[name].lastDate || s.lesson_timestamp > map[name].lastDate)) {
          map[name].lastDate = s.lesson_timestamp;
        }
        const eng = (s.student_engagement_level || '').toLowerCase();
        if (eng === 'high') map[name].engHigh++;
        else if (eng === 'medium') map[name].engMed++;
        else if (eng === 'low') map[name].engLow++;
      }

      const list = Object.values(map).map(s => {
        const total = s.engHigh + s.engMed + s.engLow;
        let avgEng = 'N/A';
        let engScore = null;
        if (total > 0) {
          if (s.engHigh / total >= 0.5) avgEng = 'High';
          else if ((s.engHigh + s.engMed) / total >= 0.5) avgEng = 'Medium';
          else avgEng = 'Low';
          engScore = (s.engHigh * 3 + s.engMed * 2 + s.engLow * 1) / total;
        }
        return {
          name: s.name,
          sessions: s.sessions,
          tutors: [...s.tutors],
          tutorCount: s.tutors.size,
          lastDate: s.lastDate,
          lastDateMs: s.lastDate ? new Date(s.lastDate).getTime() : null,
          avgEngagement: avgEng,
          engagementScore: engScore,
        };
      }).sort((a, b) => b.sessions - a.sessions);

      setStudents(list);
    } catch (err) {
      console.error('Error loading students:', err);
    }
    setLoading(false);
  };

  const filtered = search
    ? students.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    : students;

  const { sortedRows: sorted, sortKey, sortDir, toggleSort } = useSortableTable({
    rows: filtered,
    defaultKey: null,
    defaultDir: 'desc',
    accessors: {
      engagement: (s) => s.engagementScore,
      lastSession: (s) => s.lastDateMs,
    },
    firstDir: { name: 'asc', sessions: 'desc', tutorCount: 'desc', engagement: 'desc', lastSession: 'desc' },
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paged = sorted.slice(page * perPage, (page + 1) * perPage);

  useEffect(() => { setPage(0); }, [search, sortKey, sortDir]);

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const sortBtnClass = `group inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider transition-colors ${isDark ? 'hover:text-[#a1a1aa]' : 'hover:text-[#666]'}`;
  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <ChevronsUpDown className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3 w-3" />
      : <ChevronDown className="h-3 w-3" />;
  };
  const engColor = (eng) => {
    if (eng === 'High') return 'text-emerald-400 bg-emerald-400/10';
    if (eng === 'Medium') return 'text-amber-400 bg-amber-400/10';
    if (eng === 'Low') return 'text-red-400 bg-red-400/10';
    return isDark ? 'text-[#52525b] bg-[#18181b]' : 'text-[#999] bg-[#f5f5f5]';
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className={`h-8 w-48 rounded ${isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`} />
        <div className={`${cardClass} h-96`} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Students
        </h1>
        <span className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{students.length} students</span>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search students..."
          className={`w-full pl-10 pr-4 py-2 text-sm rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`}
        />
      </div>

      {/* Table */}
      <div className={cardClass}>
        <div className={`grid grid-cols-6 gap-4 px-5 py-3 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
          <button onClick={() => toggleSort('name')} className={`col-span-2 ${sortBtnClass} justify-self-start`}>
            Student <SortIndicator k="name" />
          </button>
          <button onClick={() => toggleSort('sessions')} className={`${sortBtnClass} justify-self-start`}>
            Sessions <SortIndicator k="sessions" />
          </button>
          <button onClick={() => toggleSort('tutorCount')} className={`${sortBtnClass} justify-self-start`}>
            Tutors <SortIndicator k="tutorCount" />
          </button>
          <button onClick={() => toggleSort('engagement')} className={`${sortBtnClass} justify-self-start`}>
            Engagement <SortIndicator k="engagement" />
          </button>
          <button onClick={() => toggleSort('lastSession')} className={`${sortBtnClass} justify-self-start`}>
            Last Session <SortIndicator k="lastSession" />
          </button>
        </div>
        <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
          {paged.length === 0 ? (
            <div className={`px-5 py-12 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              {search ? 'No students match your search' : 'No students found'}
            </div>
          ) : paged.map(s => (
            <button
              key={s.name}
              onClick={() => navigate(`/students/${encodeURIComponent(s.name)}`)}
              className={`w-full grid grid-cols-6 gap-4 px-5 py-4 items-center text-left transition-colors ${isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'}`}
            >
              <div className="col-span-2 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
                  <span className={`text-sm font-medium ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>{s.name.charAt(0)}</span>
                </div>
                <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.name}</span>
              </div>
              <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.sessions}</span>
              <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{s.tutorCount}</span>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full inline-block w-fit ${engColor(s.avgEngagement)} ${s.avgEngagement === 'N/A' ? 'cursor-help underline decoration-dotted underline-offset-2' : ''}`}
                title={s.avgEngagement === 'N/A' ? ENG_NA_TIP : undefined}
              >
                {s.avgEngagement}
              </span>
              <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                {s.lastDate ? new Date(s.lastDate).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '--'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{page + 1} of {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#18181b] disabled:opacity-30' : 'hover:bg-[#f0f0f0] disabled:opacity-30'}`}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Students;
